import React, { useState } from 'react';
import { Plus, Edit, Trash2, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable } from '@/components/shared/DataTable';
import { FormDialog, FormField } from '@/components/shared/FormDialog';
import { TypeFilter } from '@/components/shared/SearchFilter';
import { useCars } from '@/hooks/useCars';
import { useBookings } from '@/hooks/useBookings';
import { formatCurrency, formatDate } from '@/utils/format';

/**
 * Cars Management Component
 * Manage car fleet, types, and rental history
 */
export const CarsManagement = ({ API }) => {
  const {
    cars,
    carTypes,
    loading,
    createCar,
    updateCar,
    deleteCar,
    toggleCarStatus,
    getCarStatus,
    getCarTypeName
  } = useCars(API);

  const { bookings } = useBookings(API);

  const [showCarDialog, setShowCarDialog] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedCarHistory, setSelectedCarHistory] = useState(null);
  const [carBookingHistory, setCarBookingHistory] = useState([]);

  const [carForm, setCarForm] = useState({
    car_number: '',
    name: '',
    description: '',
    car_type_id: '',
    base_daily_rate: '',
    amenities: '',
    image_url: '',
    status: 'available'
  });

  // Filter cars by type
  const filteredCars = typeFilter === 'all' 
    ? cars 
    : cars.filter(c => c.car_type_id === typeFilter);

  // Get car types with counts
  const typesWithCounts = carTypes.map(type => ({
    ...type,
    count: cars.filter(c => c.car_type_id === type.id).length
  }));

  // Table columns
  const columns = [
    {
      key: 'car_number',
      label: 'Car Number',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-semibold text-gray-900">{value}</div>
      )
    },
    {
      key: 'name',
      label: 'Name',
      render: (value, car) => (
        <div>
          <div className="text-sm font-semibold text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">{car.description}</div>
        </div>
      )
    },
    {
      key: 'car_type_id',
      label: 'Type',
      render: (value) => (
        <div className="text-xs text-gray-500">{getCarTypeName(value)}</div>
      )
    },
    {
      key: 'amenities',
      label: 'Amenities',
      render: (amenities) => (
        <div className="flex flex-wrap gap-1">
          {amenities && amenities.length > 0 ? (
            amenities.map((amenity, idx) => (
              <span key={idx} className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                {amenity}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">None</span>
          )}
        </div>
      )
    },
    {
      key: 'base_daily_rate',
      label: 'Daily Rate',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">{formatCurrency(value)}/day</div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (status, car) => {
        const currentStatus = getCarStatus(car.id, bookings || []);
        const displayStatus = status === 'unavailable' ? 'unavailable' : currentStatus;
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            displayStatus === 'rented' 
              ? 'bg-yellow-100 text-yellow-700'
              : displayStatus === 'unavailable'
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {displayStatus === 'rented' ? 'Currently Rented' : 
             displayStatus === 'unavailable' ? 'Unavailable' : 'Available'}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (_, car) => {
        const currentStatus = getCarStatus(car.id, bookings || []);
        const displayStatus = car.status === 'unavailable' ? 'unavailable' : currentStatus;
        
        return (
          <div className="flex justify-end gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleViewHistory(car)}
              title="View booking history"
            >
              <CalendarIcon className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => toggleCarStatus(car.id)}
              title={car.status === 'available' ? 'Mark as unavailable' : 'Mark as available'}
              disabled={displayStatus === 'rented'}
            >
              {car.status === 'available' ? '🟢' : '🔴'}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleEdit(car)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleDelete(car.id)}
              disabled={displayStatus === 'rented'}
            >
              <Trash2 className={`w-4 h-4 ${displayStatus === 'rented' ? 'text-gray-300' : 'text-red-500'}`} />
            </Button>
          </div>
        );
      }
    }
  ];

  const handleEdit = (car) => {
    setEditingCar(car.id);
    setCarForm({
      car_number: car.car_number,
      name: car.name,
      description: car.description,
      car_type_id: car.car_type_id,
      base_daily_rate: car.base_daily_rate,
      amenities: car.amenities ? car.amenities.join(', ') : '',
      image_url: car.image_url || '',
      status: car.status
    });
    setShowCarDialog(true);
  };

  const handleDelete = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;
    await deleteCar(carId);
  };

  const handleViewHistory = (car) => {
    // Get bookings for this car
    const history = (bookings || []).filter(b => b.car_id === car.id);
    setSelectedCarHistory(car);
    setCarBookingHistory(history);
    setShowHistoryDialog(true);
  };

  const handleSubmit = async () => {
    if (editingCar) {
      await updateCar(editingCar, carForm);
    } else {
      await createCar(carForm);
    }
    
    setShowCarDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingCar(null);
    setCarForm({
      car_number: '',
      name: '',
      description: '',
      car_type_id: '',
      base_daily_rate: '',
      amenities: '',
      image_url: '',
      status: 'available'
    });
  };

  const handleDialogClose = (open) => {
    setShowCarDialog(open);
    if (!open) resetForm();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold">Manage Cars</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredCars.length} car{filteredCars.length !== 1 ? 's' : ''}
              {typeFilter !== 'all' ? ' filtered' : ' total'}
            </p>
          </div>
          
          {/* Type Filter */}
          <TypeFilter
            types={typesWithCounts}
            selectedType={typeFilter}
            onTypeChange={setTypeFilter}
            label="Filter by type:"
            allLabel="All Car Types"
          />
        </div>

        <Button 
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
          onClick={() => setShowCarDialog(true)}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Car
        </Button>
      </div>

      {/* Cars Table */}
      <DataTable
        columns={columns}
        data={filteredCars}
        testIdPrefix="car"
        emptyState={
          <div className="text-center text-gray-500">
            <p>No cars added yet. Click "Add Car" to get started.</p>
          </div>
        }
      />

      {/* Car Form Dialog */}
      <FormDialog
        open={showCarDialog}
        onOpenChange={handleDialogClose}
        title={editingCar ? 'Edit Car' : 'Add New Car'}
        onSubmit={handleSubmit}
        submitLabel={editingCar ? 'Update Car' : 'Create Car'}
        size="2xl"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Car Number" required htmlFor="car_number">
            <Input
              id="car_number"
              placeholder="e.g., SED-01"
              value={carForm.car_number}
              onChange={(e) => setCarForm({ ...carForm, car_number: e.target.value })}
              required={!editingCar}
            />
          </FormField>

          <FormField label="Car Name" required htmlFor="car_name">
            <Input
              id="car_name"
              placeholder="e.g., Toyota Camry"
              value={carForm.name}
              onChange={(e) => setCarForm({ ...carForm, name: e.target.value })}
              required={!editingCar}
            />
          </FormField>
        </div>

        <FormField label="Car Type" required htmlFor="car_type_id">
          <select
            id="car_type_id"
            value={carForm.car_type_id}
            onChange={(e) => setCarForm({ ...carForm, car_type_id: e.target.value })}
            required={!editingCar}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select car type</option>
            {carTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Description" required htmlFor="car_description">
          <Textarea
            id="car_description"
            placeholder="Brief description of the car"
            value={carForm.description}
            onChange={(e) => setCarForm({ ...carForm, description: e.target.value })}
            required={!editingCar}
            rows={3}
          />
        </FormField>

        <FormField label="Base Daily Rate (€)" required htmlFor="base_daily_rate">
          <Input
            id="base_daily_rate"
            type="number"
            step="0.01"
            placeholder="e.g., 50.00"
            value={carForm.base_daily_rate}
            onChange={(e) => setCarForm({ ...carForm, base_daily_rate: e.target.value })}
            required={!editingCar}
          />
        </FormField>

        <FormField label="Amenities (comma-separated)" htmlFor="amenities">
          <Input
            id="amenities"
            placeholder="e.g., GPS, Bluetooth, Child Seat"
            value={carForm.amenities}
            onChange={(e) => setCarForm({ ...carForm, amenities: e.target.value })}
          />
        </FormField>
      </FormDialog>

      {/* Booking History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCarHistory ? `Booking History - ${selectedCarHistory.name}` : 'Booking History'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {carBookingHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No booking history for this car yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {carBookingHistory.map((booking) => {
                  const isActive = booking.payment_status === 'paid' && 
                    new Date(booking.car_start_date) <= new Date() && 
                    new Date(booking.car_end_date) >= new Date();
                  
                  return (
                    <Card key={booking.id} className={`${isActive ? 'border-yellow-400 border-2' : ''}`}>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <div className="text-xs text-gray-500">Guest</div>
                            <div className="font-semibold">{booking.guest_name}</div>
                            <div className="text-xs text-gray-600">{booking.guest_email}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Room Booking</div>
                            <div className="text-sm">{booking.room_names || 'N/A'}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Car Rental Period</div>
                            <div className="text-sm">
                              {formatDate(booking.car_start_date)} - {formatDate(booking.car_end_date)}
                            </div>
                            <div className="text-xs text-gray-600">
                              {Math.ceil((new Date(booking.car_end_date) - new Date(booking.car_start_date)) / (1000 * 60 * 60 * 24))} days
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Car Amount</div>
                            <div className="font-semibold text-blue-600">{formatCurrency(booking.car_amount || 0)}</div>
                            <div className="text-xs">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                isActive 
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : booking.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {isActive ? 'Active' : booking.payment_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};